import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import {
  loadQuestions as loadQuestionsFromStorage,
  checkAndPerformDailyReset,
} from '../services/storageService';
import { CATEGORIES_LIST } from '../constants/categories';

const QuestionsContext = createContext();

/**
 * Provider que gerencia cache global de questões
 * Evita múltiplas chamadas ao AsyncStorage, melhorando performance
 * 
 * IMPORTANTE: Este cache NÃO afeta os dados salvos no dispositivo.
 * As questões continuam armazenadas no AsyncStorage de forma segura.
 */
export const QuestionsProvider = ({ children }) => {
  const [questions, setQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Carrega questões do storage (apenas na primeira vez ou quando refresh é chamado)
  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await loadQuestionsFromStorage();
      setQuestions(data);
      setLastUpdated(Date.now());
    } catch (error) {

    } finally {
      setIsLoading(false);
    }
  }, []);

  // Carrega na inicialização e executa reset diário da repetição espaçada
  useEffect(() => {
    const init = async () => {
      // Executa reset diário da repetição espaçada (se for um novo dia)
      await checkAndPerformDailyReset();
      // Carrega questões
      await refresh();
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // refresh é estável (useCallback com [])

  // Estatísticas por categoria (memoizado para evitar recálculos)
  const categoryStats = useMemo(() => {
    const stats = {};
    CATEGORIES_LIST.forEach((cat) => {
      stats[cat.id] = questions.filter((q) => q.category === cat.id).length;
    });
    return stats;
  }, [questions]);

  // Total de questões
  const questionCount = useMemo(() => questions.length, [questions]);

  // Filtra questões por categorias
  const getQuestionsByCategories = useCallback((categories) => {
    if (!categories || categories.length === 0) return questions;
    return questions.filter((q) => categories.includes(q.category));
  }, [questions]);

  const value = useMemo(() => ({
    questions,
    questionCount,
    categoryStats,
    isLoading,
    lastUpdated,
    refresh,
    getQuestionsByCategories,
  }), [questions, questionCount, categoryStats, isLoading, lastUpdated, refresh, getQuestionsByCategories]);

  return (
    <QuestionsContext.Provider value={value}>
      {children}
    </QuestionsContext.Provider>
  );
};

export const useQuestions = () => {
  const context = useContext(QuestionsContext);
  if (!context) {
    throw new Error('useQuestions deve ser usado dentro de QuestionsProvider');
  }
  return context;
};

export default QuestionsContext;

